import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi as api } from "@/lib/api";
import { BellRing, Send, Eye, RotateCcw, AlertTriangle } from "lucide-react";

const TOPICS = [
  "wyv_news", "election_reminders", "municipality_alerts", "ward_alerts",
  "new_candidate_alerts", "accountability_investigations", "pita_updates", "ontario_election_news",
];

const STATUS_STYLE = {
  queued: "bg-blue-50 text-blue-800 border-blue-200",
  simulated_sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-red-50 text-red-800 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function AdminAlerts() {
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ subject: "", body: "", topic: "wyv_news", municipality: "", ward: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/alerts/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/admin/alerts/messages", { params: statusFilter !== "all" ? { status: statusFilter } : {} })
      .then((r) => setMessages(r.data)).catch(() => {});
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const compose = async (previewOnly) => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/alerts/compose", {
        subject: form.subject, body: form.body, topic: form.topic,
        municipality: form.municipality || null, ward: form.ward || null,
        preview_only: previewOnly,
      });
      if (previewOnly) {
        setPreview(r.data);
      } else {
        toast.success(`Queued ${r.data.queued} message(s) — processed in ${r.data.delivery_mode} mode`);
        setPreview(null);
        setForm({ subject: "", body: "", topic: "wyv_news", municipality: "", ward: "" });
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compose failed");
    } finally {
      setBusy(false);
    }
  };

  const retry = async (id) => {
    try {
      await api.post(`/admin/alerts/messages/${id}/retry`);
      toast.success("Message re-queued and processed");
      load();
    } catch (e) {
      toast.error("Retry failed");
    }
  };

  return (
    <div data-testid="admin-alerts">
      <div className="mb-4">
        <h1 className="font-heading font-bold text-2xl text-foreground">Alerts</h1>
        <p className="text-sm text-muted-foreground mt-1">Targeted subscriber alerts by topic, municipality and ward.</p>
      </div>

      <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="alerts-simulated-banner">
        <CardContent className="p-4 flex items-start gap-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p>
            <strong>Delivery mode: SIMULATED.</strong> No real emails are sent — messages are queued, matched and
            logged exactly as they would be, then marked <em>simulated_sent</em>. Real delivery stays off until an
            email provider is deliberately connected.
          </p>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="alerts-summary">
          <Card><CardContent className="p-4"><p className="text-2xl font-heading font-bold" data-testid="alerts-subscribers">{summary.subscribers_active}</p><p className="text-xs font-medium">Active subscribers</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-heading font-bold">{summary.messages.queued}</p><p className="text-xs font-medium">Queued</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-heading font-bold" data-testid="alerts-simulated-count">{summary.messages.simulated_sent}</p><p className="text-xs font-medium">Simulated sent</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-heading font-bold">{summary.messages.failed}</p><p className="text-xs font-medium">Failed</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-heading font-bold uppercase">{summary.delivery_mode}</p><p className="text-xs font-medium">Delivery mode</p></CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="compose">
        <TabsList>
          <TabsTrigger value="compose" data-testid="tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-delivery-log">Delivery log</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-4">
          <Card className="max-w-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="mb-1.5 block">Topic</Label>
                  <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger className="bg-white" data-testid="compose-topic"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {TOPICS.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="compose-muni" className="mb-1.5 block">Municipality (optional)</Label>
                  <Input id="compose-muni" value={form.municipality} onChange={(e) => setForm({ ...form, municipality: e.target.value })} placeholder="e.g. Kawartha Lakes" data-testid="compose-municipality" />
                </div>
                <div>
                  <Label htmlFor="compose-ward" className="mb-1.5 block">Ward (optional)</Label>
                  <Input id="compose-ward" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} placeholder="e.g. Ward 5" data-testid="compose-ward" />
                </div>
              </div>
              <div>
                <Label htmlFor="compose-subject" className="mb-1.5 block">Subject</Label>
                <Input id="compose-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="compose-subject" />
              </div>
              <div>
                <Label htmlFor="compose-body" className="mb-1.5 block">Body</Label>
                <Textarea id="compose-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="compose-body" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => compose(true)} disabled={busy} data-testid="compose-preview-button">
                  <Eye className="h-4 w-4 mr-1.5" /> Preview recipients
                </Button>
                <Button onClick={() => compose(false)} disabled={busy || !preview} data-testid="compose-queue-button">
                  <Send className="h-4 w-4 mr-1.5" /> Queue ({preview ? preview.matched : "?"})
                </Button>
              </div>
              {preview && (
                <div className="rounded-lg border border-slate-200 bg-secondary p-4" data-testid="compose-preview-result">
                  <p className="text-sm font-medium text-foreground">
                    {preview.matched} subscriber(s) match · delivery mode: {preview.delivery_mode}
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {preview.sample.map((s, i) => (
                      <li key={i}>{s.email}{s.municipality ? ` · ${s.municipality}` : ""}{s.ward ? ` · ${s.ward}` : ""}</li>
                    ))}
                  </ul>
                  {preview.matched === 0 && <p className="text-xs text-amber-700 mt-2">No subscribers match this targeting — adjust topic/municipality/ward.</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-sm">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white" data-testid="log-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="simulated_sent">Simulated sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2" data-testid="alerts-log-list">
            {messages.map((m) => (
              <Card key={m.id} data-testid={`alert-msg-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{m.subject}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        To: {m.to_email} · topic: {m.topic}
                        {m.municipality ? ` · ${m.municipality}` : ""}{m.ward ? ` · ${m.ward}` : ""}
                        {" · "}{new Date(m.created_at).toLocaleString("en-CA")}
                        {" · attempts: "}{m.attempts}
                      </p>
                      {m.last_error && <p className="text-[11px] text-red-700 mt-0.5">{m.last_error}</p>}
                      {m.note && <p className="text-[11px] text-muted-foreground mt-0.5">{m.note}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[m.status] || ""}`}>{m.status.replace(/_/g, " ")}</Badge>
                      {m.status === "failed" && (
                        <Button size="sm" variant="outline" onClick={() => retry(m.id)} data-testid={`retry-${m.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {messages.length === 0 && (
              <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2" data-testid="alerts-log-empty">
                <BellRing className="h-4 w-4" /> No alert messages yet. Approving an imported candidate or composing an alert will queue targeted messages here.
              </CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
